import RootStore from 'stores/RootStore'

import { getEntryPointPageName, getSavedPage } from 'utils/entrypoint'
import { logInfo, logWarning } from 'utils/log'
import { UrlParams } from 'utils/url/url.interface'
import { StartupCommand } from './command.interface'

export enum EntryPoint {
  FIRST = 'first',
  VAS = 'vas',
  VAS_LEAD = 'vasLead',
  BASE_RECEIVED = 'baseReceived',
  DEBTOR_COUNT_AND_RELATIONSHIP_RECEIVED = 'debtorCountAndRelationshipReceived',
  CONTACT_RECEIVED = 'contactReceived',
  FURTHEST = 'furthest',
  FINAL = 'final',
  LEAD_SAVED = 'leadSaved',
  LEAD_ABANDONED = 'leadAbandoned',
  LEAD_ABORTED = 'leadAborted'
}

export class LoadEntryPointCommand implements StartupCommand {
  name = 'LoadEntryPoint'
  dependencies = ['LoadConfiguration', 'InitNavigation']
  timeout = 15000

  private handleFirst = async (
    rootStore: RootStore,
    forcedEntryPoint?: boolean
  ) => {
    const {
      page: { getFirstDisplayedPageName },
      navigation: { setInitiallySeenPage, setCurrentPageInSession },
      router
    } = rootStore
    const pageName = getFirstDisplayedPageName()

    if (forcedEntryPoint) {
      /* setting the page name if entry point */
      setCurrentPageInSession(pageName)
      router.replace(pageName)
      return
    }

    setInitiallySeenPage(pageName)
    await this.goToPage(getFirstDisplayedPageName(), rootStore)
  }

  private handleFurthest = async (rootStore: RootStore) => {
    const {
      page: { getLastDisplayedPageName },
      navigation: { setInitiallySeenPage }
    } = rootStore
    const pageName = getLastDisplayedPageName()

    setInitiallySeenPage(pageName)
    await this.goToPage(getLastDisplayedPageName(), rootStore)
  }

  private goToPageWithPath = (path: string) => async (rootStore: RootStore) => {
    const {
      page: { findFirstPageWithPath },
      navigation: { setInitiallySeenPage }
    } = rootStore
    const pageName = await findFirstPageWithPath(path)

    if (!pageName) {
      await this.handleFurthest(rootStore)
      return
    }

    setInitiallySeenPage(pageName)
    await this.goToPage(pageName, rootStore)
  }

  private handleFinal = async (rootStore: RootStore) => {
    const {
      traversal: { finalize }
    } = rootStore

    try {
      await finalize()
    } catch (error) {
      await this.handleFurthest(rootStore)
    }
  }

  private entryPoints: Record<
    EntryPoint,
    (rootStore: RootStore) => Promise<void>
  > = {
    [EntryPoint.FIRST]: this.handleFirst,
    [EntryPoint.VAS]: this.handleFirst,
    [EntryPoint.VAS_LEAD]: this.handleFirst,
    [EntryPoint.BASE_RECEIVED]: this.goToPageWithPath('debtors.count'),
    [EntryPoint.DEBTOR_COUNT_AND_RELATIONSHIP_RECEIVED]: this.goToPageWithPath(
      'debtors.primary.contact.email'
    ),
    [EntryPoint.CONTACT_RECEIVED]: this.goToPageWithPath(
      'debtors.primary.personal.birthday'
    ),
    [EntryPoint.FURTHEST]: this.handleFurthest,
    [EntryPoint.LEAD_ABANDONED]: this.handleFurthest,
    [EntryPoint.LEAD_ABORTED]: this.handleFurthest,
    [EntryPoint.LEAD_SAVED]: this.handleFurthest,
    [EntryPoint.FINAL]: this.handleFinal
  }

  private goToPage = async (pageName: string, rootStore: RootStore) => {
    const {
      router,
      navigation: { setCurrentPageInSession }
    } = rootStore
    const resolvedPageName = await getEntryPointPageName(pageName, rootStore)

    if (pageName !== resolvedPageName) {
      logWarning(`"${pageName}" resolved to "${resolvedPageName}"!`)
    }

    setCurrentPageInSession(resolvedPageName)
    router.replace(resolvedPageName)
  }

  execute = async (rootStore: RootStore, urlParams?: UrlParams) => {
    const savedPage = getSavedPage(rootStore)
    const { initialEntryPoint } = rootStore
    const entryPoint = urlParams?.entryPoint

    if (entryPoint) {
      /* handling the entry point from url's handler */
      const handler = this.entryPoints[entryPoint]

      if (handler) {
        logInfo(`Loading entry point from url "${entryPoint}"..`)
        await handler(rootStore, true)
        return
      }
    }

    if (savedPage && initialEntryPoint !== 'final') {
      logInfo(`Reloading current page "${savedPage}"..`)

      await this.goToPage(savedPage, rootStore)
      return
    }

    logInfo(`Loading entry point "${initialEntryPoint}"..`)

    const handler = this.entryPoints[initialEntryPoint]
    if (handler) {
      await handler(rootStore)
      return
    }

    await this.goToPage(initialEntryPoint, rootStore)
  }
}

export default LoadEntryPointCommand
